import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildRepos } from "@/core/data/repos";
import { registrarAuditoria } from "@/core/audit/registrarAuditoria";
import { notificarTransicionContrato, notificarIpcContrato } from "@core/mail/cableado";
import { useGerenciaActiva, useVerTodasLasGerencias } from "@core/gerenciaActiva";
import type { ContratoMarco } from "@app-types/entities";
import type { EstadoContrato } from "@constants/estados";

/**
 * Lista de contratos marco filtrada por GERENCIA ACTIVA (Fase 3 del ADR).
 * Los contratos pertenecen a una gerencia; no admiten sharing.
 */
export function useContratos(tenantId: string) {
  const gerenciaActiva = useGerenciaActiva();
  const verTodos = useVerTodasLasGerencias();
  return useQuery({
    queryKey: ["contratos", tenantId, gerenciaActiva, verTodos ? "all" : "filtered"],
    queryFn: async () => {
      const contratos = await buildRepos(tenantId).contrato.list();
      if (verTodos) return contratos;
      return contratos.filter((c) => c.gerenciaId === gerenciaActiva);
    },
    staleTime: 60_000,
  });
}

export function useContratosVigentes(tenantId: string) {
  const gerenciaActiva = useGerenciaActiva();
  const verTodos = useVerTodasLasGerencias();
  return useQuery({
    queryKey: [
      "contratos",
      tenantId,
      "vigentes",
      gerenciaActiva,
      verTodos ? "all" : "filtered",
    ],
    queryFn: async () => {
      const contratos = await buildRepos(tenantId).contrato.listVigentes();
      if (verTodos) return contratos;
      return contratos.filter((c) => c.gerenciaId === gerenciaActiva);
    },
    staleTime: 60_000,
  });
}

export function useContrato(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["contrato", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).contrato.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useTarifasContrato(tenantId: string, contratoId: string | null) {
  return useQuery({
    queryKey: ["tarifas", tenantId, contratoId],
    queryFn: () =>
      contratoId
        ? buildRepos(tenantId).contrato.getTarifas(contratoId)
        : Promise.resolve([]),
    enabled: !!contratoId,
  });
}

export function useCreateContrato(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<ContratoMarco, "id" | "createdAt" | "updatedAt" | "version">) =>
      buildRepos(tenantId).contrato.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contratos", tenantId] });
    },
  });
}

/**
 * Elimina un contrato marco de forma DEFINITIVA. La UI debe garantizar antes:
 * (a) que quien elimina es el creador del contrato o un rol global, y
 * (b) que el contrato no tiene OTs ni MRs ligadas (referencias colgantes).
 * Queda trazado en el audit log.
 */
export function useEliminarContrato(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      codigo,
      userEmail,
    }: {
      id: string;
      codigo: string;
      userEmail: string;
    }) => {
      await buildRepos(tenantId).contrato.delete(id);
      void registrarAuditoria(tenantId, {
        usuario: userEmail,
        accion: "CONTRATO_ELIMINADO",
        entidad: "CONTRATO",
        entidadId: codigo,
        diff: { eliminado: true },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contratos", tenantId] });
    },
  });
}

export function useUpdateContrato(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      version,
      userEmail,
      userName,
    }: {
      id: string;
      data: Partial<ContratoMarco>;
      version: number;
      /** Email del usuario para CM-2 (incremento IPC). Opcional. */
      userEmail?: string;
      userName?: string;
    }) => {
      const repo = buildRepos(tenantId).contrato;
      const antes = await repo.get(id);
      await repo.update(id, data, version);
      // CM-2: si se añadió un IPC nuevo en `incrementosIpc`, notifica.
      const despues = await repo.get(id);
      if (despues && antes && userEmail) {
        const antesLen = antes.incrementosIpc?.length ?? 0;
        const despuesLen = despues.incrementosIpc?.length ?? 0;
        if (despuesLen > antesLen) {
          const ultimoIpc = despues.incrementosIpc?.[despuesLen - 1];
          if (ultimoIpc) {
            void registrarAuditoria(tenantId, {
              usuario: userEmail,
              accion: "CONTRATO_INCREMENTO_IPC",
              entidad: "CONTRATO",
              entidadId: despues.codigo ?? id,
              diff: { porcentaje: ultimoIpc.porcentaje },
            });
            void notificarIpcContrato({
              tenantId,
              contrato: despues,
              porcentaje: ultimoIpc.porcentaje,
              actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
            });
          }
        }
      }
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["contrato", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["contratos", tenantId] });
    },
  });
}

export function useChangeContratoState(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nextState,
      userEmail,
      userName,
    }: {
      id: string;
      nextState: EstadoContrato;
      userEmail: string;
      userName?: string;
    }) => {
      const repo = buildRepos(tenantId).contrato;
      const antes = await repo.get(id);
      const estadoAnterior = antes?.estado;
      await repo.changeState(id, nextState, userEmail);
      const despues = await repo.get(id);
      if (despues && estadoAnterior) {
        void registrarAuditoria(tenantId, {
          usuario: userEmail,
          accion: `CONTRATO_${nextState}`,
          entidad: "CONTRATO",
          entidadId: despues.codigo ?? id,
          diff: {
            estadoAnterior,
            estadoNuevo: nextState,
          },
        });
        void notificarTransicionContrato({
          tenantId,
          contrato: despues,
          estadoAnterior,
          estadoNuevo: nextState,
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contratos", tenantId] });
    },
  });
}
